import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Slider, Box, useMediaQuery, useTheme } from '@mui/material';
import FileUploader, { FILE_TYPE } from '../file-uploader';
import { getCroppedImg } from './getCroppedImage';
import Button from '../button';
import { Crop, CroppedAreaPixels, ImageCropperProps } from './types';

const ImageCropper = ({ mainActionTitle, secondaryActionTitle, onSave, onCancel }: ImageCropperProps) => {
    const theme = useTheme();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);
    const [imageFormat, setImageFormat] = useState<string>('');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const onCropComplete = useCallback((_: Crop, croppedAreaPixels: CroppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleImageUpload = (file: File | null) => {
        if (file) {
            setImageFormat(file.type.split('/')[1]);
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!croppedAreaPixels || !imageSrc) return;
        const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, zoom, imageFormat);
        const croppedFile = new File([croppedBlob], `cropped-image.${imageFormat}`, { type: `image/${imageFormat}` });
        const formData = new FormData();
        formData.append('file', croppedFile);
        onSave(formData);
    };

    return (
        <Box>
            <Box sx={{ width: isMobile ? '275px' : '400px', height: '100%' }}>
                {!imageSrc && <FileUploader type={FILE_TYPE.IMAGE} handleFileUpload={handleImageUpload} />}
                {imageSrc && (
                    <>
                        <div style={{ position: 'relative', height: isMobile ? 200 : 300 }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape='round'
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                style={{
                                    containerStyle: {
                                        overflow: 'hidden',
                                    },
                                }}
                            />
                        </div>

                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby='zoom-slider'
                            onChange={(e, zoom) => setZoom(zoom as number)}
                            valueLabelDisplay='off'
                            sx={{
                                '& .MuiSlider-thumb': {
                                    color: 'common.primary',
                                },
                                '& .MuiSlider-track': {
                                    backgroundColor: 'common.primary',
                                },
                                '& .MuiSlider-rail': {
                                    backgroundColor: 'common.grey1',
                                },
                            }}
                        />
                    </>
                )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', padding: '24px 0', gap: '6px' }}>
                <Button label={secondaryActionTitle} size='large' variant='outlined' onClick={onCancel} />
                <Button label={mainActionTitle} size='large' variant='primary' type='submit' onClick={handleUpload} />
            </Box>
        </Box>
    );
};

export default ImageCropper;
